import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  useDeleteExternalAccount,
  useRenewAccess,
  useUpdateExternalAccount,
} from "@/features/external-accounts/hooks/use-external-accounts";
import { IntegrationConfigurator } from "@/components/molecules/integration-configurator";
import {
  isBookingProvider,
  type ExternalAccount,
} from "@/features/external-accounts/types";
import {
  fieldsFromAccount,
  INTEGRATION_OF,
} from "@/features/integrations/booking-accounts";
import { useBookingAccountUsage } from "@/features/integrations/hooks/use-booking-accounts";
import { useTranslation } from "@/features/utils/i18n";
import {
  ArrowsClockwise,
  DotsThree,
  Pause,
  PencilSimple,
  Play,
  Trash,
} from "@phosphor-icons/react";
import { useState } from "react";
import { toast } from "sonner";
import { EditAccountDialog } from "./edit-dialog";

export const Action = ({ account }: { account: ExternalAccount }) => {
  const { t } = useTranslation("externalAccounts");
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  // A booking account is edited in its configurator (checked again on save);
  // the plain dialog only renames it.
  const [configuratorOpen, setConfiguratorOpen] = useState(false);
  const booking = isBookingProvider(account.provider);
  const { data: usage = {}, refetch: refetchUsage } = useBookingAccountUsage();

  const deleteAccount = useDeleteExternalAccount();
  const updateAccount = useUpdateExternalAccount();
  const renewAccess = useRenewAccess();

  const inUse = (users: { name?: string }[]) =>
    t("externalAccounts.messages.deleteInUse", {
      users: users.map((u) => u.name || t("table.company")).join(", "),
    });

  const handleDelete = async () => {
    // Deleting the account a module books through leaves it with no credentials:
    // the first booking of the next call would fail in front of a customer.
    const users = booking ? usage[account.id] || [] : [];
    if (users.length) {
      toast.error(inUse(users));
      return;
    }
    try {
      await deleteAccount.mutateAsync(account.id);
      toast.success(t("externalAccounts.messages.accountDeleted"));
    } catch (error) {
      // The server checks it too: the usage read above may be older than the
      // configuration it refused on.
      if (error instanceof Error && error.message === "account_in_use") {
        const { data: fresh = {} } = await refetchUsage();
        toast.error(
          fresh[account.id]?.length
            ? inUse(fresh[account.id])
            : t("externalAccounts.messages.deleteError"),
        );
        return;
      }
      toast.error(
        error instanceof Error
          ? error.message
          : t("externalAccounts.messages.deleteError"),
      );
    }
  };

  const handleToggleStatus = async () => {
    const newStatus = account.status === "active" ? "inactive" : "active";
    try {
      await updateAccount.mutateAsync({
        id: account.id,
        data: { status: newStatus },
      });
      toast.success(
        newStatus === "active"
          ? t("externalAccounts.messages.accountActivated")
          : t("externalAccounts.messages.accountDeactivated"),
      );
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : t("externalAccounts.messages.updateError"),
      );
    }
  };

  const handleRenew = async () => {
    try {
      const result = await renewAccess.mutateAsync(account.id);
      if (result.success) {
        toast.success(t("externalAccounts.messages.accessRenewed"));
      } else {
        toast.error(result.error || t("externalAccounts.messages.renewError"));
      }
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : t("externalAccounts.messages.renewFailed"),
      );
    }
  };

  return (
    <div className="text-right flex items-center justify-end gap-2">
      <Button
        variant="ghost"
        size="icon"
        onClick={() =>
          booking ? setConfiguratorOpen(true) : setEditDialogOpen(true)
        }
      >
        <PencilSimple className="h-4 w-4" weight="fill" />
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon">
            <DotsThree className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {booking ? (
            <DropdownMenuItem onClick={() => setEditDialogOpen(true)}>
              <PencilSimple className="h-4 w-4 mr-2" />
              {t("externalAccounts.actions.rename")}
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem onClick={handleRenew}>
              <ArrowsClockwise className="h-4 w-4 mr-2" />
              {t("externalAccounts.actions.renewAccess")}
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onClick={handleToggleStatus}>
            {account.status === "active" ? (
              <>
                <Pause className="h-4 w-4 mr-2" />
                {t("externalAccounts.actions.deactivate")}
              </>
            ) : (
              <>
                <Play className="h-4 w-4 mr-2" />
                {t("externalAccounts.actions.activate")}
              </>
            )}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={handleDelete}
            className="text-red-600 focus:text-red-600"
          >
            <Trash className="h-4 w-4 mr-2" />
            {t("externalAccounts.actions.delete")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {booking && (
        <IntegrationConfigurator
          integration={
            INTEGRATION_OF[account.provider as keyof typeof INTEGRATION_OF]
          }
          open={configuratorOpen}
          onOpenChange={setConfiguratorOpen}
          accountId={account.id}
          initialValues={fieldsFromAccount(account)}
        />
      )}

      <EditAccountDialog
        account={account}
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
      />
    </div>
  );
};
